/**
 * The phone's **Day** and **3 days** — Aaron, Oct 25, phone notes 3.4, 5, 6
 * and 7, Outlook's day view:
 *
 * - the whole day, 12 AM to 12 AM, scrolling in itself, a solid line each
 *   hour and a fainter one each half hour (note 6), opening at the start of
 *   the working day (the desktop's rule — `openingMinutes`) or, late in the
 *   day, with now in view; the now line on today;
 * - things as the desktop draws them (§6.2 — `StopBlock`, `TaskChip`,
 *   `TimeBlock`: time, title, city, the drive note), overlapping ones sharing
 *   the width; a tap opens what a tap opens on the phone (the stop page, the
 *   Task sheet — `openCalendarItem`);
 * - Day for one person (its day line, *Thu, Oct 22 · 4 stops · about 52 min
 *   driving*, is the page bar's sub since Aaron's Oct 27 note 1.1.1 —
 *   `phone-sub.ts`); for several a narrow column each (§6.4, Board 13 B);
 *   3 days three columns under the strip that heads them, everyone shown on
 *   them in their colour;
 * - edge to edge (Aaron, Oct 27, notes 1.1.2 and 1.2.1): no card round the
 *   hours — no margin, corners or border — from the strip to the tab bar and
 *   side to side;
 * - **touch and hold** an empty slot (note 5): after 400 ms without moving an
 *   hour's ghost appears there, follows the finger snapped to 15 minutes —
 *   the hours scroll under it near their top and bottom — and where it is let
 *   go it is in edit mode, its handles and **Next** opening New meeting for
 *   that time and that person (Aaron, Oct 27, answer 6). A quick tap does
 *   nothing; a finger that moves first is a scroll;
 * - **touch and hold** something the viewer may move (Aaron, Oct 27, note
 *   1.1.4 — Outlook's): it lifts and the finger drags it to another time, day
 *   or person; held and let go without moving it, it is in edit mode, its two
 *   handles changing its start and end (`phone-lift.tsx`). A quick tap still
 *   opens it; a finger that moves first is still a scroll.
 */
import { useRef, useState, type ReactNode } from 'react'
import {
  entriesOn, toneOf,
  type ActivityDay, type CalendarAnswer, type CalendarDrive, type CalendarItem, type CalendarPersonDays, type ShowState,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Face } from '../faces.js'
import { StopBlock, TaskChip, TimeBlock } from './blocks.js'
import { BLOCK_GAP, GRID_METRICS, span } from './look.js'
import { openCalendarItem } from './open-item.js'
import { TimeGrid, type GridColumn } from './time-grid.js'
import {
  HOLD_TO_CREATE_MS, HOLD_TO_LIFT_MS, NEW_MINUTES, axisOf, draggedEdge, draggedStart, heldStart, liftable, liftedStart,
  phoneLanes, swipeOf, type LiftPlace,
} from './phone-time.js'
import { followFinger } from './phone-finger.js'
import { LiftedBlock, type PhoneLift } from './phone-lift.js'

/** A slot being made: whose, which day, the start, and how long when edit mode changed it from an hour. */
export interface PhoneSlot { personId: string; date: string; start: number; minutes?: number }

const metrics = GRID_METRICS.phone

/** Where a 3-day strip's days start and end so each is over its column: the hours' axis, and the edge (no card — Oct 27). */
export const threeDayInset = { left: metrics.axis, right: 0 }

const isStop = (item: CalendarItem) => item.kind === 'appointment' || item.kind === 'meeting'

/** One column's worth: an item and whose calendar it is on. */
interface Entry { item: CalendarItem; person: CalendarPersonDays }

function OffChip({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-5 items-center truncate rounded-[5px] bg-line-soft px-[7px] text-[11px] font-semibold text-text-muted">
      {children}
    </span>
  )
}

/** The hour about to be a meeting: dashed, *9:00–10:00*, *New meeting* (the desktop's ghost, Board 6 D). */
function Ghost({ start, minutes = NEW_MINUTES, scale, title }: { start: number; minutes?: number; scale: number; title: string }) {
  return (
    <div data-new-ghost="" className="pointer-events-none absolute overflow-hidden rounded-[7px] border-2 border-dashed border-accent bg-accent-soft/80 py-1 pl-2.5 pr-[7px] leading-[normal]"
      style={{ top: start * scale, height: minutes * scale - BLOCK_GAP, left: 3, right: 3, zIndex: 8 }}>
      <span className="tnum block whitespace-nowrap text-[10.5px] font-semibold text-text-muted">{span(start, start + minutes)}</span>
      <b className="mt-px block truncate text-[12px] leading-[1.25]">{title}</b>
    </div>
  )
}

export function PhoneDay({
  answer, days, today, now, several, byPerson, show, startAt, scrollTo, slot, canBook, meId, lift, liftDrives = null, onLift, onDrop,
  onTick, onStep,
}: {
  answer: CalendarAnswer
  /** The days shown: one, or three. */
  days: string[]
  today: string
  now: number
  several: boolean
  /** Day with several people: a column per person. */
  byPerson: boolean
  show: ShowState
  /** Where the hours open (local minutes). */
  startAt: number
  scrollTo: { minutes: number; nonce: number } | null
  /** New meeting is open for this hour: its ghost stays drawn. */
  slot: PhoneSlot | null
  /** Whether the viewer may book for this person (§5.3). */
  canBook: (personId: string) => boolean
  meId: string
  /** What a finger has lifted, or the new hour in edit mode (`phone-lift.tsx`). */
  lift: PhoneLift | null
  /** The drives where a carried stop is going — its own and the next one's — as the server re-worked them. */
  liftDrives?: CalendarDrive[] | null
  onLift: (next: PhoneLift | null) => void
  /** Let go somewhere else: written as the desktop writes a drag. */
  onDrop: (lift: PhoneLift) => void
  onTick: (item: CalendarItem) => void
  /** A swipe sideways across the hours: the day (or three) after or before, as Outlook's. */
  onStep?: (direction: 1 | -1) => void
}) {
  const scale = metrics.scale
  const [holding, setHolding] = useState<PhoneSlot | null>(null)
  const ghost = holding ?? slot

  /* A column's key says whose and which day, for a ghost dragged across columns. */
  const target = (key: string): { personId: string; date: string } | null => {
    if (byPerson) return answer.people.some((p) => p.id === key) ? { personId: key, date: days[0]! } : null
    return days.includes(key) ? { personId: slotPerson(), date: key } : null
  }
  /** On a column of days, whose the new meeting is: the viewer's when they are shown, else the first shown they may book for. */
  const slotPerson = (): string => {
    const shown = answer.people.map((p) => p.id)
    if (shown.includes(meId) && canBook(meId)) return meId
    return shown.find(canBook) ?? shown[0] ?? meId
  }

  /** The lift as it is now — read by a press, which outlives the render that made its handler. */
  const liftNow = useRef(lift)
  liftNow.current = lift
  /* And what letting go does, as the page says it now — not as it said it when the finger went down. */
  const dropNow = useRef(onDrop)
  dropNow.current = onDrop
  /** This press became a hold: whatever it does sideways is the ghost's, never a swipe. */
  const holdArmed = useRef(false)
  /** A click the browser sends after a hold or a swipe ended is not a tap on what it lands on. */
  const swallowUntil = useRef(0)
  const swallowClicks = () => { swallowUntil.current = performance.now() + 400 }

  /** The columns' keys in their order: days, or people on Day side by side. */
  const keys = byPerson ? answer.people.map((p) => p.id) : days
  const keyOf = (place: LiftPlace) => (byPerson ? place.personId : place.date)
  /** The column under a finger — never the lifted thing it carries, which is drawn from another column. */
  const columnUnder = (x: number, y: number): string | null => {
    for (const element of document.elementsFromPoint(x, y)) {
      if (element.closest('[data-lifted], [data-handle]')) continue
      const column = element.closest('[data-column]')
      if (column) return column.getAttribute('data-column')
    }
    return null
  }
  /** Where a lifted thing lands in a column: that day for its person, or that person on the day. */
  const placeOf = (key: string | null, personId: string): LiftPlace | null => {
    if (key === null) return null
    if (byPerson) return answer.people.some((p) => p.id === key) ? { personId: key, date: days[0]! } : null
    return days.includes(key) ? { personId, date: key } : null
  }

  /**
   * A press in empty time. Where it is comes from the point itself, never the event's target:
   * a phone's browser hands a touch just under a stop to the stop (it snaps a finger to the
   * nearest thing that answers taps), so the column is asked what is really under the finger.
   */
  const pressEmpty = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    const under = document.elementsFromPoint(event.clientX, event.clientY)
    if (under.some((element) => element.closest('[data-stop], [data-block], [data-task], [role="dialog"]'))) return
    const column = under.map((element) => element.closest<HTMLElement>('[data-column]')).find(Boolean)
    const hours = column?.firstElementChild
    const at = column ? target(column.getAttribute('data-column') ?? '') : null
    const scroller = column?.closest<HTMLElement>('[data-grid-scroll]')
    if (!hours || !at || !scroller) return
    startHold(at.personId, at.date, (event.clientY - hours.getBoundingClientRect().top) / scale, event, scroller)
  }

  const startHold = (personId: string, date: string, minutes: number, event: React.PointerEvent<HTMLElement>, scroller: HTMLElement) => {
    if (!canBook(personId)) return
    const held = heldStart(minutes)
    let made: PhoneSlot = { personId, date, start: held }
    const show = (next: PhoneSlot) => { made = next; setHolding(next) }
    followFinger({
      at: event, scroller, holdMs: HOLD_TO_CREATE_MS,
      onArm: () => {
        holdArmed.current = true
        navigator.vibrate?.(8)
        show({ personId, date, start: held })
      },
      /* It moves with the finger, across columns too, and the hours scroll under it at their edge. */
      onFollow: ({ x, y, dy }) => {
        const at = target(columnUnder(x, y) ?? '') ?? made
        show({ personId: at.personId, date: at.date, start: draggedStart(held, dy, scale) })
      },
      onEnd: ({ armed, how }) => {
        setHolding(null)
        if (!armed) return
        swallowClicks()
        if (how !== 'up') return
        /* Let go: edit mode — an hour, its handles, and Next (Aaron, Oct 27, answer 6). */
        const place: LiftPlace = { personId: made.personId, date: made.date }
        onLift({ item: null, home: keyOf(place), from: place, to: place, start: made.start, end: made.start + NEW_MINUTES, mode: 'edit' })
      },
    })
  }

  /**
   * Touch and hold something the viewer may move (note 1.1.4): it lifts, and
   * follows the finger in 15-minute steps to another time, day or person, the
   * hours scrolling under it at their edge. Let go there, it is written; let
   * go without moving it, it is in edit mode. Before it lifts, a finger that
   * moves is a scroll and a quick one is a tap, which opens it.
   */
  const pressItem = (entry: Entry, home: string, event: React.PointerEvent<HTMLElement>) => {
    if (liftNow.current || (event.pointerType === 'mouse' && event.button !== 0)) return
    const { item, person } = entry
    if (!liftable(item)) return
    /* A touch just beside it that the browser handed to it is empty time's (`pressEmpty`), not a hold on it. */
    if (!document.elementsFromPoint(event.clientX, event.clientY).includes(event.currentTarget)) return
    const scroller = event.currentTarget.closest<HTMLElement>('[data-grid-scroll]')
    if (!scroller) return
    const from: LiftPlace = { date: item.date, personId: person.id }
    const length = item.end - item.start
    let next: PhoneLift = { item, home, from, to: from, start: item.start, end: item.end, mode: 'drag' }
    followFinger({
      at: event, scroller, holdMs: HOLD_TO_LIFT_MS,
      onArm: () => {
        holdArmed.current = true
        navigator.vibrate?.(8)
        onLift(next)
      },
      onFollow: ({ x, y, dy }) => {
        const start = liftedStart(item.start, dy, scale, length)
        next = { ...next, to: placeOf(columnUnder(x, y), from.personId) ?? next.to, start, end: start + length }
        onLift(next)
      },
      onEnd: ({ armed, moved, how }) => {
        if (!armed) return
        swallowClicks()
        if (how !== 'up') onLift(null)
        else if (moved) dropNow.current(next)
        else onLift({ ...next, mode: 'edit' })
      },
    })
  }

  /**
   * Edit mode (answer 6): the block itself drags at once — no hold, it is
   * already lifted — and its handles move its start or its end, in 15-minute
   * steps, never shorter than a quarter hour.
   */
  const pressLifted = (part: 'move' | 'start' | 'end', event: React.PointerEvent<HTMLElement>) => {
    const base = liftNow.current
    if (!base || base.mode !== 'edit' || (event.pointerType === 'mouse' && event.button !== 0)) return
    /* The hours' own press — a hold, a swipe — is not this one. */
    event.stopPropagation()
    const scroller = event.currentTarget.closest<HTMLElement>('[data-grid-scroll]')
    if (!scroller) return
    const length = base.end - base.start
    let next = base
    followFinger({
      at: event, scroller, holdMs: 0,
      onFollow: ({ x, y, dy }) => {
        if (part === 'move') {
          const to = placeOf(columnUnder(x, y), base.to.personId) ?? next.to
          const start = liftedStart(base.start, dy, scale, length)
          /* A new hour goes only to someone the viewer may book for. */
          next = { ...base, to: base.item || canBook(to.personId) ? to : next.to, start, end: start + length }
        } else {
          next = { ...base, ...draggedEdge(base, part, dy, scale) }
        }
        onLift(next)
      },
      onEnd: ({ moved }) => { if (moved) swallowClicks() },
    })
  }

  /* A swipe across the hours turns the day; a hold that moved first is no hold, and a swipe never taps what it ended on. */
  const swipe = {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      if (event.pointerType === 'mouse') return
      holdArmed.current = false
      const x0 = event.clientX
      const y0 = event.clientY
      let axis: 'x' | 'y' | null = null
      const move = (next: PointerEvent) => { axis ??= axisOf(next.clientX - x0, next.clientY - y0) }
      const up = (ended: PointerEvent) => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        window.removeEventListener('pointercancel', up)
        if (axis !== 'x' || ended.type !== 'pointerup' || holdArmed.current) return
        const direction = swipeOf(ended.clientX - x0, ended.clientY - y0)
        if (!direction) return
        swallowClicks()
        onStep?.(direction)
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
      window.addEventListener('pointercancel', up)
    },
    onClickCapture: (event: React.MouseEvent) => {
      if (performance.now() > swallowUntil.current) return
      event.preventDefault()
      event.stopPropagation()
    },
  }

  const visible = (item: CalendarItem) => (isStop(item) ? show.visits : item.kind === 'task' ? show.tasks : show.blocks)
  const ghostTitle = (personId: string) => {
    if (personId === meId) return 'New meeting'
    const first = answer.people.find((p) => p.id === personId)?.firstName
    return first ? `New meeting for ${first}` : 'New meeting'
  }

  /** While something is lifted a tap opens nothing — the bar's Done or Cancel finishes first. */
  const open = (item: CalendarItem) => { if (!liftNow.current) openCalendarItem(item) }
  /** Where the lifted thing came from, in the column it came from: faded while it is carried, gone once it is saved. */
  const leftBehind = (item: CalendarItem, key: string): React.CSSProperties | null => {
    if (!lift?.item || lift.item.key !== item.key || lift.home !== key) return null
    return { opacity: lift.mode === 'saving' ? 0 : 0.35 }
  }

  /** A column's things, placed: lanes for what overlaps, each drawn as the desktop draws it. */
  const place = (key: string, entries: Entry[], narrow: boolean, colourOf: ((person: CalendarPersonDays) => string) | null,
    dayOf: (entry: Entry) => ActivityDay | undefined) => {
    const shown = entries.filter(({ item }) => !item.allDay && visible(item))
    const tasks = shown.filter(({ item }) => item.kind === 'task')
    const rest = phoneLanes(shown.filter(({ item }) => item.kind !== 'task').map((entry) => ({ ...entry, start: entry.item.start, end: entry.item.end })))
    return (
      <>
        {rest.map(({ item, person, lane, lanes }) => {
          const style = {
            top: item.start * scale,
            left: `calc(3px + (100% - 6px) * ${lane} / ${lanes})`,
            width: `calc((100% - 6px) / ${lanes} - ${lanes > 1 ? 2 : 0}px)`,
            ...leftBehind(item, key),
          }
          const edge = colourOf ? colourOf(person) : undefined
          const press = (event: React.PointerEvent<HTMLElement>) => pressItem({ item, person }, key, event)
          if (item.kind === 'block') {
            return <TimeBlock key={item.key} item={item} scale={scale} narrow={narrow || lanes > 1} edge={edge} style={style}
              onPointerDown={press} resizable={false} />
          }
          /* Each person's own drive to their own stop (§6.3) — where a stop is being carried, as it would be with it there. */
          const drives = liftDrives && lift && keyOf(lift.to) === key && lift.to.personId === person.id ? liftDrives
            : dayOf({ item, person })?.drives
          const drive = show.drives ? drives?.find((d) => d.key === item.key) ?? null : null
          return (
            <StopBlock key={item.key} item={item} drive={drive} edge={edge} scale={scale} style={style}
              narrow={narrow || lanes > 1} narrowTags showFaces={!narrow && lanes === 1}
              onPointerDown={press} resizable={false} onClick={() => open(item)} />
          )
        })}
        {tasks.map(({ item, person }) => (
          <TaskChip key={item.key} item={item} style={{ top: item.start * scale, left: 3, right: 3, ...leftBehind(item, key) }}
            onPointerDown={(event) => pressItem({ item, person }, key, event)}
            onTick={() => { if (!liftNow.current) onTick(item) }} onClick={() => open(item)} />
        ))}
        {liftIn(key)}
      </>
    )
  }
  /** The lifted thing, drawn from the column it was lifted in and moved across to the one it is over. */
  const liftIn = (key: string) => {
    if (!lift || lift.home !== key) return null
    const over = keys.indexOf(keyOf(lift.to))
    if (over < 0) return null
    const person = answer.people.find((p) => p.id === lift.to.personId)
    return (
      <LiftedBlock lift={lift} columns={over - keys.indexOf(key)} scale={scale} narrow={byPerson || days.length > 1}
        edge={(byPerson || several) && person ? toneOf(person.colour).edge : undefined}
        drive={show.drives && lift.item ? liftDrives?.find((d) => d.key === lift.item!.key) ?? null : null}
        title={ghostTitle(lift.to.personId)} onPress={pressLifted} />
    )
  }
  const allDayOf = (day: ActivityDay | undefined, named: boolean) => {
    if (!day) return null
    const chips: ReactNode[] = []
    if (!day.working && day.offWords) chips.push(<OffChip key="off">{named ? day.offWords : 'Off'}</OffChip>)
    for (const item of day.items) if (item.allDay && show.blocks) chips.push(<OffChip key={item.key}>Time off</OffChip>)
    return chips.length > 0 ? chips : null
  }
  const pastOf = (day: string): GridColumn['past'] => (day < today ? 'all' : day === today ? 'until-now' : 'none')
  const ghostIn = (personId: string | null, date: string) =>
    ghost && ghost.date === date && (personId === null || ghost.personId === personId)
      ? <Ghost start={ghost.start} minutes={ghost.minutes} scale={scale} title={ghostTitle(ghost.personId)} /> : null

  let columns: GridColumn[]
  if (byPerson) {
    const date = days[0]!
    columns = answer.people.map((person) => {
      const day = person.days.find((d) => d.date === date)
      return {
        key: person.id,
        head: (
          <span className="flex items-center gap-[5px]">
            <Face person={person} size={20} />
            <b className="text-[12px]">{person.firstName}</b>
          </span>
        ),
        allDay: allDayOf(day, false),
        past: pastOf(date),
        today: date === today,
        off: day ? !day.working : false,
        body: (
          <>
            {place(person.id, (day?.items ?? []).map((item) => ({ item, person })), true, (p) => toneOf(p.colour).edge, () => day)}
            {ghostIn(person.id, date)}
          </>
        ),
      }
    })
  } else {
    const one = answer.people.length === 1 ? answer.people[0]! : null
    columns = days.map((date) => {
      const own = one?.days.find((d) => d.date === date)
      const entries: Entry[] = one ? (own?.items ?? []).map((item) => ({ item, person: one }))
        : entriesOn(answer, date, show)
      return {
        key: date,
        head: null,
        allDay: one ? allDayOf(own, days.length === 1) : null,
        past: pastOf(date),
        today: date === today,
        off: own ? !own.working : false,
        body: (
          <>
            {place(date, entries, days.length > 1, several ? (p) => toneOf(p.colour).edge : null,
              (entry) => entry.person.days.find((d) => d.date === date))}
            {ghostIn(null, date)}
          </>
        ),
      }
    })
  }

  return (
    /* A held finger makes things here: no text selection, no callout, no long-press menu. */
    <div data-phone-day=""
      /* Sideways is ours (a swipe), up and down the hours' own scroll; side by side scrolls sideways itself. */
      className={cn('flex h-full min-h-0 flex-col select-none [-webkit-touch-callout:none]',
        !byPerson && onStep && '[&_[data-grid-scroll]]:[touch-action:pan-y]')}
      onContextMenu={(event) => event.preventDefault()}
      onPointerDown={(event) => {
        /* Something is lifted: the hours scroll, and nothing else starts until it is done. */
        if (liftNow.current) return
        pressEmpty(event)
        if (!byPerson && onStep) swipe.onPointerDown(event)
      }}
      onClickCapture={swipe.onClickCapture}>
      <TimeGrid key={byPerson ? 'people' : days.length} columns={columns} startHour={0} endHour={24} now={now}
        scale={scale} axisWidth={metrics.axis} headHeight={byPerson ? metrics.head : 0} allDayWhenEmpty={false}
        fill flush halfHours initialMinutes={startAt} scrollTo={scrollTo}
        columnWidth={byPerson && columns.length > 1 ? metrics.personColumn : undefined} />
    </div>
  )
}

/** Whose calendar a day of the strip is read from: the one person shown, or nobody's in particular. */
export function stripInfo(answer: CalendarAnswer | null, day: string, show: ShowState) {
  if (!answer) return null
  const weather = answer.dates.find((d) => d.date === day)?.weather ?? null
  const one = answer.people.length === 1 ? answer.people[0] : null
  const own = one?.days.find((d) => d.date === day)
  if (!answer.dates.some((d) => d.date === day)) return null
  const count = entriesOn(answer, day, show).filter(({ item }) => !item.allDay).length
  return { weather, count, off: own ? !own.working : false }
}
